import React from 'react';
import merge from 'deepmerge';
import classNames from 'classnames';
import { Observe } from 'rewire-core';
import { WithStyle } from 'rewire-ui';
import Button from '@material-ui/core/Button';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import { MuiThemeProvider, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import { Backburger as BackburgerIcon, Forwardburger as ForwardburgerIcon } from 'mdi-material-ui';
import FlowLogoFullWhite from '../images/flow-logo-full-white.svg';
import FlowLogoGear from '../images/flow-logo-clock.svg';
import Routes from '../Routes';
import InviteDialog from './InviteDialog';
import inviteDialogModel from './InviteDialogModel';
import SidePanelContents from './SidePanelContents';
import mainPageModel from '../main-navigation/MainPageModel';
import sideTogglePanelModel from './SideTogglePanelModel';
import { withStyles } from '../utility/styles';
import { DefaultTheme } from '@material-ui/styles';
import { Link } from '../utility/Link';

const theme = (outerTheme: Theme) => ({
  ...outerTheme,
  props: merge(outerTheme.props as any, {
    MuiAccordionSummary: {
      expandIcon: <ArrowDropDownIcon />,
    },
    MuiListItem: {
      disableGutters: true,
      button: true,
    },
    MuiButton: {
      disableRipple: true,
    },
    MuiIconButton: {
      color: 'primary',
      disableRipple: true,
    },
  }),
  overrides: merge(outerTheme.overrides as any, {
    MuiAccordion: {
      root: {
        width: '100%',
        backgroundColor: 'inherit',
        boxShadow: 'none',
        '&$expanded': {
          margin: '0px',
          minHeight: '0px',
        },
      },
    },
    MuiAccordionSummary: {
      root: {
        minHeight: '0px',
        padding: '0px',
        '&$expanded': {
          minHeight: '0px',
        },
        '&:focus': {
          background: 'none',
          outline: 'none',
        },
      },
      content: {
        minHeight: '0px',
        margin: '5px 0px',
        paddingLeft: '30px',
        alignItems: 'center',
        justifyContent: 'space-between',
        '&$expanded': {
          margin: '5px 0px',
        },
        '&>:last-child': {
          paddingRight: '0px',
        },
      },
      expandIcon: {
        position: 'absolute',
        top: '18px',
        left: '3px',
        transform: 'translateY(-50%) rotate(-90deg)',
        '&$expanded': {
          transform: 'translateY(-50%) rotate(0deg)',
        },
      },
    },
    MuiAccordionDetails: {
      root: {
        display: 'block',
        padding: '0px',
      },
    },
    MuiListItem: {
      root: {
        '&[role="button"]': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingLeft: '30px',
          cursor: 'pointer',
        },
      },
      container: {
        width: '100%',
      },
      dense: {
        paddingTop: '6px',
        paddingBottom: '6px',
      },
      button: {
        '&:hover, &:active': {
          backgroundColor: outerTheme.palette.primary.dark,
        },
      },
      secondaryAction: {
        paddingRight: '0px',
      },
    },
    MuiListItemSecondaryAction: {
      root: {
        right: '0px',
      },
    },
    MuiButton: {
      root: {
        minWidth: '0px',
      },
    },
    MuiButtonBase: {
      root: {
        '&$focusVisible': {
          backgroundColor: `${outerTheme.palette.primary.dark} !important`,
          outline: 'none !important',
          color: `${outerTheme.palette.navBarSecondary.light} !important`,
        },
      },
    },
    MuiIconButton: {
      colorPrimary: {
        color: outerTheme.palette.navBarSecondary.main,
        '&:hover, &:active': {
          color: outerTheme.palette.navBarSecondary.light,
        },
      },
    },
    MuiDivider: {
      root: {
        margin: '5px 0px',
        backgroundColor: outerTheme.palette.navBarSecondary.main,
      },
    },
    MuiBadge: {
      badge: {
        color: outerTheme.palette.navBarPrimary.main,
        backgroundColor: outerTheme.palette.error.main,
        width: '16px',
        height: '16px',
        top: '-1px',
        right: '-22px',
      },
    },
    MuiTypography: {
      subtitle1: {
        color: outerTheme.palette.navBarPrimary.main,
      },
      body2: {
        color: outerTheme.palette.navBarSecondary.main,
      },
    },
  }),
});

export type Anchor = 'left' | 'right';

const sideToggleStyles = (theme: Theme) => ({
  root: {
    flexShrink: 0,
    color: theme.palette.navBarSecondary.main,
  },
  drawerPaper: {
    backgroundColor: theme.palette.primary.main,
    width: '275px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  drawerOpen: {
    width: '275px',
    padding: '0px 1px 0px 0px',
    transition: theme.transitions.create(['width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shorter,
    }),
  },
  drawerMinimized: {
    width: '44px',
    padding: '0px',
    justifyContent: 'space-between',
    transition: theme.transitions.create(['width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shorter,
    }),
  },
  flowLogo: {
    width: '72px',
  },
  flowLogoGear: {
    width: '32px',
  },
  flowLogoLink: {
    lineHeight: 0,
  },
  flowLogoGearLink: {
    marginBottom: '15px',
  },
  logoOpenContainer: {
    display: 'flex',
    alignSelf: 'flex-end',
    width: '44px',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px 0px 0px 0px',
  },
  logoCloseContainer: {
    display: 'flex',
    flexShrink: 0,
    minHeight: '32px',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 10px 14px 32px',
    borderBottom: `1px solid ${theme.palette.navBarSecondary.main}`,
  },
  toggleButton: {
    width: 'initial',
    height: 'initial',
    color: theme.palette.navBarSecondary.main,
    backgroundColor: 'transparent !important',
    '&:hover, &:active': {
      color: theme.palette.navBarSecondary.light,
    },
  },
  closeButtonIcon: {
    fontSize: '1.75rem',
  },
  openButtonIcon: {
    fontSize: '1.75rem',
  },
  inviteContainer: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    borderTop: `1px solid ${theme.palette.navBarSecondary.main}`,
  },
  inviteButton: {
    width: '100%',
    textTransform: 'none',
    padding: '12px 16px',
    borderRadius: 'unset',
    background: theme.palette.primary.dark,
    '&:hover': {
      color: theme.palette.navBarPrimary.main,
      background: theme.palette.primary.dark,
    },
  },
  inviteButtonNarrowPanel: {
    minWidth: '0',
    padding: '4px 0px',
  },
  inviteIcon: {
    margin: '0px 0px 0px 16px',
  },
  inviteIconNarrowPanel: {
    margin: '0px 0px 0px 0px',
    height: '32px',
  },
});

interface ISideTogglePanelProps {
  anchor: Anchor;
}

type SideTogglePanelProps = WithStyle<ReturnType<typeof sideToggleStyles>, ISideTogglePanelProps>;

class SideTogglePanel extends React.PureComponent<SideTogglePanelProps> {
  inviteToWorkSight = () => {
    if (!mainPageModel.canAddUsers) return;
    inviteDialogModel.load();
  };

  handleOpen = () => {
    if (!sideTogglePanelModel.isLocked) {
      sideTogglePanelModel.open();
    }
  };

  handleClose = () => {
    if (!sideTogglePanelModel.isLocked) {
      sideTogglePanelModel.close();
    }
  };

  renderHeader = React.memo((props: { open: boolean }) => {
    const { classes } = this.props;
    const { open } = props;

    if (open) {
      return (
        <div className={classes.logoCloseContainer}>
          <Link to={Routes.Home} className={classes.flowLogoLink}>
            <img className={classes.flowLogo} src={FlowLogoFullWhite} alt="Flow" />
          </Link>
          {!sideTogglePanelModel.isLocked && (
            <IconButton className={classes.toggleButton} arial-label="Close" onClick={this.handleClose}>
              <BackburgerIcon className={classes.closeButtonIcon} />
            </IconButton>
          )}
        </div>
      );
    }
    // side panel not open
    return (
      <div className={classes.logoOpenContainer}>
        <Link to={Routes.Home} className={classNames(classes.flowLogoLink, classes.flowLogoGearLink)}>
          <img className={classes.flowLogoGear} src={FlowLogoGear} alt="Flow" />
        </Link>
        {!sideTogglePanelModel.isLocked && (
          <IconButton className={classes.toggleButton} arial-label="Open" onClick={this.handleOpen}>
            <ForwardburgerIcon className={classes.openButtonIcon} />
          </IconButton>
        )}
      </div>
    );
  });

  renderInviteButton = React.memo((props: { open: boolean }) => {
    const { classes } = this.props;
    const { open } = props;
    if (!mainPageModel.canAddUsers) return null;

    if (open) {
      return (
        <Button className={classes.inviteButton} disableRipple onClick={this.inviteToWorkSight}>
          <Typography>Invite to Flow</Typography>
          <PersonAddIcon className={classes.inviteIcon} />
        </Button>
      );
    }
    // side panel not open
    return (
      <Button
        title="Invite to Flow"
        className={classNames(classes.inviteButton, classes.inviteButtonNarrowPanel)}
        disableRipple
        onClick={this.inviteToWorkSight}
      >
        <PersonAddIcon className={classes.inviteIconNarrowPanel} />
      </Button>
    );
  });

  render() {
    const { classes } = this.props;

    return (
      <Observe
        render={() => {
          const { isOpen } = sideTogglePanelModel;

          return (
            <Drawer
              className={classNames(classes.root, isOpen ? classes.drawerOpen : classes.drawerMinimized)}
              classes={{ paper: classNames(classes.drawerPaper, isOpen ? classes.drawerOpen : classes.drawerMinimized) }}
              anchor={this.props.anchor}
              open={isOpen}
              variant="permanent"
            >
              <MuiThemeProvider theme={theme as DefaultTheme}>
                <this.renderHeader open={isOpen} />
                <SidePanelContents open={isOpen} />
                <this.renderInviteButton open={isOpen} />
              </MuiThemeProvider>
              <InviteDialog />
            </Drawer>
          );
        }}
      />
    );
  }
}

export default withStyles<ISideTogglePanelProps>(sideToggleStyles, SideTogglePanel);
